import { cn } from '@/lib/cn';
import { useEffect, useState, type ReactNode } from 'react';
import { Upload, Plus, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  FONT_SLOTS, FONT_LABEL, ROLES, familyFor, kitBlob, loadKitFonts, roleRule, uploadKitFile, emptyPerson,
  type AssetRule, type BrandKit, type FontSlot, type KitPerson, type Service,
} from '@/lib/brand-kit';
import { ROLE_LABEL } from './TemplateEditor';

const ASSET_CHOICES: [AssetRule, string][] = [['brand', 'Brand asset (managers only)'], ['flexible', 'Flexible (team can change)']];

type Set = (fn: (k: BrandKit) => void) => void;

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card className="mb-4">
      <h2 className="font-medium text-muted-strong">{title}</h2>
      {hint && <p className="text-xs text-muted mt-0.5 mb-3">{hint}</p>}
      <div className={hint ? '' : 'mt-3'}>{children}</div>
    </Card>
  );
}

function Area({ label, value, onChange, dir, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; dir?: 'rtl'; rows?: number }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs text-muted">{label}</span>
      <textarea dir={dir} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-sm text-muted-strong focus:outline-none focus:border-accent-400" />
    </label>
  );
}

/** A kit image (logo, photo, reference): thumbnail through the API, and an upload button. */
export function KitImage({ clientId, path, slot, onPath, label, dark, readOnly }: {
  clientId: string; path: string; slot: string; onPath: (p: string) => void; label: string; dark?: boolean; readOnly?: boolean;
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    if (path) kitBlob(clientId, path).then((b) => live && setUrl(URL.createObjectURL(b))).catch(() => setUrl(''));
    else setUrl('');
    return () => { live = false; };
  }, [clientId, path]);
  const pick = async (f?: File) => {
    if (!f) return;
    setBusy(true);
    try { onPath(await uploadKitFile(clientId, slot, f)); toast.success(`${label} uploaded`); }
    catch (e) { toast.error((e as Error).message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted">{label}</span>
      <div className={`h-28 rounded-lg border border-slate-800 flex items-center justify-center overflow-hidden ${dark ? 'bg-slate-700' : 'bg-white/90'}`}>
        {url ? <img src={url} alt={label} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-500">none yet</span>}
      </div>
      {!readOnly && <div className="flex gap-2">
        <label className="inline-flex items-center gap-1 text-xs text-accent-400 cursor-pointer hover:underline">
          <Upload size={12} /> {busy ? 'Uploading...' : path ? 'Replace' : 'Upload'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy}
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {path && <button className="text-xs text-muted hover:text-red-400" onClick={() => onPath('')}>Remove</button>}
      </div>}
    </div>
  );
}

function FontRow({ clientId, kit, slot, set, readOnly }: { clientId: string; kit: BrandKit; slot: FontSlot; set: Set; readOnly?: boolean }) {
  const f = kit.type.fonts[slot] ?? { path: '', family: '', google: '' };
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(0);
  useEffect(() => { loadKitFonts(clientId, kit).then(() => setReady((n) => n + 1)); }, [clientId, f.path, f.google]); // eslint-disable-line react-hooks/exhaustive-deps
  const ar = slot.startsWith('ar');
  const pick = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadKitFile(clientId, `font:${slot}`, file);
      set((k) => { k.type.fonts[slot] = { path, family: file.name.replace(/\.[^.]+$/, ''), google: '' }; });
      toast.success(`${FONT_LABEL[slot]} uploaded`);
    } catch (e) { toast.error((e as Error).message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[170px_1fr_220px] gap-3 items-center py-2 border-b border-slate-800/60" data-testid={`font-${slot}`}>
      <div>
        <div className="text-sm text-muted-strong">{FONT_LABEL[slot]}</div>
        <div className="text-xs text-muted">{slot.endsWith('primary') ? 'headings' : slot.endsWith('secondary') ? 'body' : 'accents, captions'}</div>
      </div>
      <div key={ready} dir={ar ? 'rtl' : 'ltr'} className="text-2xl text-white truncate" style={{ fontFamily: `"${familyFor(kit, slot)}"` }}>
        {f.path || f.google ? (ar ? 'وجهك بين أيدٍ أمينة' : 'Your face in trusted hands') : <span className="text-sm text-slate-500">no font yet</span>}
        {f.family && <span className="block text-xs text-muted" style={{ fontFamily: 'inherit' }}>{f.family}</span>}
      </div>
      {readOnly ? <div /> : <div className="flex flex-col gap-1">
        <label className="inline-flex items-center gap-1 text-xs text-accent-400 cursor-pointer hover:underline">
          <Upload size={12} /> {busy ? 'Uploading...' : f.path ? 'Replace font file' : 'Upload font file'}
          <input type="file" accept=".woff2,.woff,.ttf,.otf" className="hidden" disabled={busy}
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <input placeholder="or a Google Fonts name" value={f.google} disabled={!!f.path}
          onChange={(e) => set((k) => { k.type.fonts[slot] = { ...f, google: e.target.value }; })}
          className="h-8 rounded border border-slate-800 bg-bg px-2 text-xs text-muted-strong disabled:opacity-40" />
        {f.path && <button className="text-left text-xs text-muted hover:text-red-400" onClick={() => set((k) => { delete k.type.fonts[slot]; })}>Remove</button>}
      </div>}
    </div>
  );
}

function Swatch({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs text-muted">{label}</span>
      <span className="flex items-center gap-2">
        <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 rounded border border-slate-800 bg-bg" />
        <input value={value} placeholder="#RRGGBB" onChange={(e) => onChange(e.target.value)}
          className="h-10 w-28 rounded-lg border border-slate-800 bg-bg px-2 text-sm text-muted-strong font-mono" />
      </span>
    </label>
  );
}

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

/** The brand identity. Only clients.write changes it (Aziz 2026-10-22); everyone else on the
 *  clinic sees it read-only: a disabled fieldset, and no upload, add or remove controls. */
export default function KitForm({ clientId, kit, people, set: setKit, readOnly }: { clientId: string; kit: BrandKit; people: KitPerson[]; set: Set; readOnly?: boolean }) {
  const set: Set = readOnly ? () => undefined : setKit;
  const id = kit.identity;
  const I = (key: keyof BrandKit['identity'], label: string, ar = false) => (
    <Input label={label} dir={ar ? 'rtl' : undefined} value={id[key]} onChange={(e) => set((k) => { k.identity[key] = e.target.value; })} />
  );
  const person = (tid: string, name: string) => kit.people[tid] ?? emptyPerson(name);
  const P = (tid: string, name: string, key: keyof ReturnType<typeof emptyPerson>, label: string, ar = false) => (
    <Input label={label} dir={ar ? 'rtl' : undefined} value={String(person(tid, name)[key] ?? '')}
      onChange={(e) => set((k) => { k.people[tid] = { ...person(tid, name), [key]: e.target.value }; })} />
  );
  const setService = (i: number, patch: Partial<Service>) => set((k) => { k.services[i] = { ...k.services[i], ...patch }; });

  return (
    <fieldset disabled={readOnly} className="min-w-0 border-0 p-0 m-0" data-testid={readOnly ? 'kit-readonly' : undefined}>
      {readOnly && <p className="mb-3 text-sm text-muted flex items-center gap-1.5"><Lock size={14} /> Only managers and people with Brand details change the brand identity.</p>}
      <Section title="Identity" hint="How the clinic names itself and where people reach it.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {I('brand_en', 'Brand name (English)')}{I('brand_ar', 'Brand name (Arabic)', true)}
          {I('tagline_en', 'Tagline (English)')}{I('tagline_ar', 'Tagline (Arabic)', true)}
          {I('whatsapp', 'WhatsApp')}{I('phone', 'Phone')}
          {I('website', 'Website')}{I('instagram', 'Instagram handle')}
          {I('address_en', 'Address (English)')}{I('address_ar', 'Address (Arabic)', true)}
          {I('hours_en', 'Opening hours (English)')}{I('hours_ar', 'Opening hours (Arabic)', true)}
        </div>
      </Section>

      <Section title="Logos" hint="PNG with a transparent background works best.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KitImage clientId={clientId} path={kit.logos.main} slot="logo_main" readOnly={readOnly} label="Main (light backgrounds)" onPath={(p) => set((k) => { k.logos.main = p; })} />
          <KitImage clientId={clientId} path={kit.logos.white} slot="logo_white" readOnly={readOnly} label="White (dark backgrounds, photos)" dark onPath={(p) => set((k) => { k.logos.white = p; })} />
          <KitImage clientId={clientId} path={kit.logos.icon} slot="logo_icon" readOnly={readOnly} label="Icon (square mark)" onPath={(p) => set((k) => { k.logos.icon = p; })} />
        </div>
      </Section>

      <Section title="Fonts" hint="Upload the clinic's own font files: three Arabic, three English. Every template box uses one of these six, nothing else.">
        {FONT_SLOTS.map((s) => <FontRow key={s} clientId={clientId} kit={kit} slot={s} set={set} readOnly={readOnly} />)}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          {(['h1', 'h2', 'body', 'caption', 'button'] as const).map((sz) => (
            <Input key={sz} type="number" label={`${sz.toUpperCase()} size (px at 1080)`} value={kit.type.sizes[sz]}
              onChange={(e) => set((k) => { k.type.sizes[sz] = Number(e.target.value) || 0; })} />
          ))}
        </div>
      </Section>

      <Section title="Colours" hint="Templates can only use these. 'Never use' colours are flagged when a design uses them.">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(['primary', 'secondary', 'accent', 'background', 'text'] as const).map((c) => (
            <Swatch key={c} label={c[0].toUpperCase() + c.slice(1)} value={kit.colours[c]} onChange={(v) => set((k) => { k.colours[c] = v; })} />
          ))}
        </div>
        <div className="mt-3">
          <Input label="Never use (comma separated #RRGGBB)" value={kit.colours.never.join(', ')}
            onChange={(e) => set((k) => { k.colours.never = e.target.value.split(',').map((x) => x.trim()).filter(Boolean); })} />
        </div>
      </Section>

      <Section title="Brand assets and flexible elements"
        hint="Brand assets keep their place, size, words and style in every design; only managers change them. Flexible elements the team can edit.">
        <div className="flex flex-col" data-testid="asset-rules">
          {[...ROLES, 'background' as const].map((r) => {
            const v = roleRule(kit, r);
            return (
              <div key={r} className="flex items-center justify-between gap-3 flex-wrap py-1.5 border-b border-slate-800/60" data-testid={`asset-rule-${r}`}>
                <span className="text-sm text-muted-strong">{ROLE_LABEL[r]}</span>
                {readOnly ? (
                  // disabled radios all draw grey: say the choice instead
                  <span className={cn('text-xs px-2 py-0.5 rounded', v === 'brand' ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/10 text-emerald-300')}
                    data-testid={`asset-rule-${r}-shown`}>{ASSET_CHOICES.find(([val]) => val === v)?.[1]}</span>
                ) : <div className="flex gap-4 text-sm text-muted-strong">
                  {ASSET_CHOICES.map(([val, label]) => (
                    <label key={val} className="flex items-center gap-1.5">
                      <input type="radio" name={`asset-${r}`} checked={v === val} data-testid={`asset-rule-${r}-${val}`}
                        onChange={() => set((k) => { k.asset_rules = { ...(k.asset_rules ?? {}), [r]: val }; })} /> {label}
                    </label>
                  ))}
                </div>}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Voice" hint="Banned words are checked on every generated static, in both languages.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Tone in three words" value={kit.voice.tone} onChange={(e) => set((k) => { k.voice.tone = e.target.value; })} />
          <Input label="Arabic dialect" value={kit.voice.dialect} onChange={(e) => set((k) => { k.voice.dialect = e.target.value; })} />
          <Input label="Signature line (English)" value={kit.voice.signature_en} onChange={(e) => set((k) => { k.voice.signature_en = e.target.value; })} />
          <Input label="Signature line (Arabic)" dir="rtl" value={kit.voice.signature_ar} onChange={(e) => set((k) => { k.voice.signature_ar = e.target.value; })} />
          <div>
            <Area label="Banned words, one per line" value={kit.voice.banned.join('\n')} onChange={(v) => set((k) => { k.voice.banned = lines(v); })} rows={5} />
            {/* Dubai health-ad rules on doctors' social media (Gulf Business, 2026) */}
            {!readOnly && <button type="button" className="text-xs text-accent-400 hover:underline mt-1" data-testid="add-uae-banned"
              onClick={() => set((k) => {
                const add = ['best', 'only', 'safest', 'magic', 'miracle', '100%', 'immediate results', "don't delay",
                  'guaranteed', 'الأفضل', 'الوحيد', 'الأكثر أماناً', 'سحري', 'معجزة', 'نتائج فورية', 'مضمون'];
                k.voice.banned = Array.from(new Set([...k.voice.banned, ...add]));
              })}>
              + Add the UAE health-ad banned words
            </button>}
          </div>
        </div>
      </Section>

      <Section title="Compliance">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Regulator (DHA, DOH or MOHAP)" value={kit.compliance.regulator} onChange={(e) => set((k) => { k.compliance.regulator = e.target.value.toUpperCase(); })} />
          <Input label="Ad permit number" value={kit.compliance.ad_permit} onChange={(e) => set((k) => { k.compliance.ad_permit = e.target.value; })} />
          <Input label="Disclaimer (English)" value={kit.compliance.disclaimer_en} onChange={(e) => set((k) => { k.compliance.disclaimer_en = e.target.value; })} />
          <Input label="Disclaimer (Arabic)" dir="rtl" value={kit.compliance.disclaimer_ar} onChange={(e) => set((k) => { k.compliance.disclaimer_ar = e.target.value; })} />
          <label className="flex items-center gap-2 text-sm text-muted-strong">
            <input type="checkbox" checked={kit.compliance.no_prices} onChange={(e) => set((k) => { k.compliance.no_prices = e.target.checked; })} />
            No prices, offers or discounts on any static
          </label>
          <label className="flex items-start gap-2 text-sm text-muted-strong" data-testid="kit-before-after">
            <input type="checkbox" className="mt-1" checked={!!kit.compliance.before_after_allowed}
              onChange={(e) => set((k) => { k.compliance.before_after_allowed = e.target.checked; })} />
            <span>Before and after allowed for this clinic
              <span className="block text-xs text-muted">Off: the before and after designs show, but nothing can be downloaded or saved. On: each one still needs both photos and the patient's signed consent ticked.</span>
            </span>
          </label>
          <Area label="Other rules, one per line" value={kit.compliance.rules.join('\n')} onChange={(v) => set((k) => { k.compliance.rules = lines(v); })} />
        </div>
      </Section>

      <Section title="People" hint="Titles exactly as on the licence. Nobody appears on a static without consent.">
        {people.length === 0 && <p className="text-sm text-muted">No doctors on this clinic yet. Add them on the clinic page first.</p>}
        {people.map(({ talent_id: tid, name }) => {
          const p = person(tid, name);
          return (
            <div key={tid} className="border-b border-slate-800/60 py-4 grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-4" data-testid={`person-${tid}`}>
              <KitImage clientId={clientId} path={p.photo} slot={`person:${tid}`} readOnly={readOnly} label="Photo" onPath={(v) => set((k) => { k.people[tid] = { ...person(tid, name), photo: v }; })} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {P(tid, name, 'name_en', 'Name (English)')}{P(tid, name, 'name_ar', 'Name (Arabic)', true)}
                {P(tid, name, 'title_en', 'Licensed title (English)')}{P(tid, name, 'title_ar', 'Licensed title (Arabic)', true)}
                {P(tid, name, 'licence', 'Licence number')}{P(tid, name, 'languages', 'Languages')}
                {P(tid, name, 'specialties_en', 'Specialties (English)')}{P(tid, name, 'specialties_ar', 'Specialties (Arabic)', true)}
                {P(tid, name, 'days_en', 'Days and hours (English)')}{P(tid, name, 'days_ar', 'Days and hours (Arabic)', true)}
                <Area label="Short bio (English)" value={p.bio_en} onChange={(v) => set((k) => { k.people[tid] = { ...person(tid, name), bio_en: v }; })} />
                <Area label="Short bio (Arabic)" dir="rtl" value={p.bio_ar} onChange={(v) => set((k) => { k.people[tid] = { ...person(tid, name), bio_ar: v }; })} />
                <label className="flex items-center gap-2 text-sm text-muted-strong">
                  <input type="checkbox" checked={p.ads_consent} onChange={(e) => set((k) => { k.people[tid] = { ...person(tid, name), ads_consent: e.target.checked }; })} />
                  Signed consent to appear in ads
                </label>
                <label className="flex items-center gap-2 text-sm text-muted-strong">
                  <input type="checkbox" checked={!p.shown} onChange={(e) => set((k) => { k.people[tid] = { ...person(tid, name), shown: !e.target.checked }; })} />
                  Never put on a static (left the clinic, or asked not to)
                </label>
              </div>
            </div>
          );
        })}
      </Section>

      <Section title="Services" hint="The menu. Sensitive items are flagged; only advertisable ones can be picked for ads.">
        {kit.services.map((s, i) => (
          <div key={s.id || i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 py-3 border-b border-slate-800/60">
            <div className="grid gap-2">
              <Input label="Service (English)" value={s.name_en} onChange={(e) => setService(i, { name_en: e.target.value })} />
              <Input label="Category (English)" value={s.category_en} onChange={(e) => setService(i, { category_en: e.target.value })} />
              <Input label="One line (English)" value={s.line_en} onChange={(e) => setService(i, { line_en: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Input label="Service (Arabic)" dir="rtl" value={s.name_ar} onChange={(e) => setService(i, { name_ar: e.target.value })} />
              <Input label="Category (Arabic)" dir="rtl" value={s.category_ar} onChange={(e) => setService(i, { category_ar: e.target.value })} />
              <Input label="One line (Arabic)" dir="rtl" value={s.line_ar} onChange={(e) => setService(i, { line_ar: e.target.value })} />
            </div>
            <div className="flex md:flex-col gap-3 text-sm text-muted-strong md:pt-6">
              <label className="flex items-center gap-2"><input type="checkbox" checked={s.advertisable} onChange={(e) => setService(i, { advertisable: e.target.checked })} /> Advertisable</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={s.sensitive} onChange={(e) => setService(i, { sensitive: e.target.checked })} /> Sensitive</label>
              {!readOnly && <button className="inline-flex items-center gap-1 text-xs text-muted hover:text-red-400" onClick={() => set((k) => { k.services.splice(i, 1); })}><Trash2 size={12} /> Remove</button>}
            </div>
          </div>
        ))}
        {!readOnly && <Button variant="secondary" size="sm" className="mt-3" icon={<Plus size={14} />}
          onClick={() => set((k) => { k.services.push({ id: '', name_en: '', name_ar: '', category_en: '', category_ar: '', line_en: '', line_ar: '', advertisable: true, sensitive: false }); })}>
          Add a service
        </Button>}
      </Section>

      <Section title="References" hint="Designs the clinic likes: mood, layout and light to follow. They also give the design previews a real photo.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kit.references.map((r, i) => (
            <div key={r.path || i} className="flex flex-col gap-2">
              <KitImage clientId={clientId} path={r.path} slot="reference" readOnly={readOnly} label={`Reference ${i + 1}`} onPath={(p) => set((k) => { if (p) k.references[i].path = p; else k.references.splice(i, 1); })} />
              <Input placeholder="What to take from it" value={r.note} onChange={(e) => set((k) => { k.references[i].note = e.target.value; })} />
            </div>
          ))}
        </div>
        {!readOnly && <label className="mt-3 inline-flex items-center gap-1 text-sm text-accent-400 cursor-pointer hover:underline">
          <Plus size={14} /> Add a reference image
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return;
            try { const p = await uploadKitFile(clientId, 'reference', f); set((k) => { k.references.push({ path: p, note: '' }); }); }
            catch (err) { toast.error((err as Error).message || 'Upload failed'); }
          }} />
        </label>}
      </Section>
    </fieldset>
  );
}
